// First-party beacon to the dark-stack collector (fcpo-gtm/dark-stack).
// Fires for every visit on a server-log basis (no fingerprint, no cookies);
// the FingerprintJS visitorId attaches only after analytics consent is granted
// (auto in non-banner regions, banner accept elsewhere) — see consent.ts.

const COLLECT_ENDPOINT =
  import.meta.env.VITE_COLLECT_ENDPOINT ?? 'https://t.fractionalcpo.com/collect';

// Public browser key — same exposure class as the PostHog key above it in the
// bundle. The server Secret API key never ships to the client.
const FP_PUBLIC_KEY = '5PNBcqDLlFJWHk4GtDuU';

let fpId: string | null = null;
let fpStarted = false;

/**
 * Consent revocation hook: stop attaching the visitor ID immediately and allow
 * a clean re-init if consent is later granted again. Without this, the
 * module-level fpId kept transmitting for the rest of the SPA session after
 * an opt-out (QA finding A1, proven live 2026-08-06).
 */
export function clearFingerprint() {
  fpId = null;
  fpStarted = false;
}

export function sendPageView() {
  // Localhost dev sessions stay out of production data unless explicitly
  // pointed at a collector via VITE_COLLECT_ENDPOINT.
  if (import.meta.env.DEV && !import.meta.env.VITE_COLLECT_ENDPOINT) return;
  const body = JSON.stringify({
    fp: fpId ?? undefined,
    url: window.location.href,
    ref: document.referrer || undefined,
  });
  try {
    if (!navigator.sendBeacon?.(COLLECT_ENDPOINT, body)) {
      fetch(COLLECT_ENDPOINT, { method: 'POST', body, keepalive: true }).catch(() => {});
    }
  } catch {
    // analytics must never break the site
  }
}

/**
 * Load FingerprintJS Pro and attach the visitorId to subsequent beacons.
 * Called from consent.ts once (and only when) analytics consent is granted.
 * Identification calls route through the first-party subdomain (ad-blocker
 * resistant, subdomain "fp" Active in the Fingerprint dashboard since
 * 2026-08-06) with Fingerprint's default endpoint as automatic fallback.
 */
export function initFingerprint() {
  if (fpStarted || !FP_PUBLIC_KEY || FP_PUBLIC_KEY.startsWith('__')) return;
  fpStarted = true;
  import('@fingerprintjs/fingerprintjs-pro')
    .then((FingerprintJS) =>
      FingerprintJS.load({
        apiKey: FP_PUBLIC_KEY,
        endpoint: ['https://fp.fractionalcpo.com', FingerprintJS.defaultEndpoint],
      }),
    )
    .then((fp) => fp.get())
    .then((result) => {
      fpId = result.visitorId;
      // Supplemental beacon so single-page visits still stitch to the visitor.
      sendPageView();
    })
    .catch(() => {
      fpStarted = false;
    });
}
